
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { format, subDays } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { TrendingUp } from "lucide-react";
import { ProgressChartSkeleton } from "./ProgressChartSkeleton";

export const ProgressChart = ({ taskType }: { taskType: number }) => {
  const { user } = useAuth();

  const { data: scores, isLoading } = useQuery({
    queryKey: ['progress-scores', taskType, user?.id],
    queryFn: async () => {
      if (!user) {
        return { data: [], improvement: 0 };
      }

      const { data: evaluations, error } = await supabase
        .from('evaluations')
        .select(`
          overall_score,
          created_at,
          drafts!inner (
            user_id, 
            topics!inner (
              id,
              task_type
            )
          )
        `)
        .eq('drafts.user_id', user.id)
        .eq('drafts.topics.task_type', taskType)
        .order('created_at');

      if (error) {
        throw error;
      }

      if (!evaluations?.length) {
        return { data: [], improvement: 0 };
      }

      const firstScore = evaluations[0].overall_score;
      const lastScore = evaluations[evaluations.length - 1].overall_score;
      const improvement = ((lastScore - firstScore) / firstScore * 100).toFixed(1);

      return {
        data: evaluations.map(evaluation => ({
          date: format(new Date(evaluation.created_at), 'MMM dd'),
          score: evaluation.overall_score
        })),
        improvement: parseFloat(improvement)
      };
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    retry: 2
  });

  // Handle loading state
  if (isLoading) {
    return <ProgressChartSkeleton taskType={taskType} />;
  }

  // Handle empty state
  if (!scores?.data.length) {
    return (
      <Card className="border border-stone-200 shadow-sm dark:bg-stone-800 dark:border-stone-700">
        <CardHeader className="px-8">
          <CardTitle className="text-xl dark:text-white">Progress Task {taskType}</CardTitle>
        </CardHeader>
        <CardContent className="px-8 pb-6">
          <div className="h-[300px] flex items-center justify-center text-sm text-stone-500 dark:text-stone-400">
            We'll put a nice chart here as soon as you give Task {taskType} a try!
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-stone-200 shadow-sm dark:bg-stone-800 dark:border-stone-700 progress-chart">
      <CardHeader className="px-8">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl dark:text-white">Progress Task {taskType}</CardTitle>
          {scores?.improvement > 0 && (
            <div className="flex items-center text-sm text-green-600">
              <TrendingUp className="h-4 w-4 mr-1" />
              {scores.improvement}% increasing score
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-8 pb-6">
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              className="text-sm text-stone-500 dark:text-stone-400"
              data={scores?.data || []}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} />
              <YAxis domain={[0, 9]} ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]} hide={true} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#F97316"
                strokeWidth={2}
                dot={{ fill: "#F97316" }}
                activeDot={{ r: 6, fill: "#F97316" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
